import { Config, ErrorHandling, TranslationConfig, Toastr, alertDialog, EntityDialog, TemplatedDialog, Culture } from "@serenity-is/corelib";
import { gridDefaults } from "@serenity-is/sleekgrid";
import flatpickr from "flatpickr";
import "flatpickr/dist/l10n";
import { getLanguageList } from "./Helpers/LanguageList";
import DOMPurify from "dompurify";

Config.rootNamespaces.push('VitSchool');
TranslationConfig.getLanguageList = getLanguageList;
gridDefaults.sanitizer = (globalThis.DOMPurify = DOMPurify).sanitize;

// Configure global Culture for VitSchool (English, dd/MM/yyyy, 12-hour clock)
Culture.dateFormat = "dd/MM/yyyy";
Culture.dateOrder = "dmy";
Culture.dateTimeFormat = "dd/MM/yyyy hh:mm:ss tt";
Culture.timeFormat = "hh:mm tt";

// Configure flatpickr defaults (dd/MM/yyyy and 12-hour AM/PM time)
flatpickr.defaultConfig.dateFormat = "d/m/Y";
flatpickr.defaultConfig.altFormat = "d/m/Y";
flatpickr.defaultConfig.time_24hr = false;

// Global Dialog Toolbar Button Filtering: Keep Save and Delete buttons only; remove Undelete, Edit, Clone, Localization buttons.
const originalEntityDialogGetToolbarButtons = EntityDialog.prototype.getToolbarButtons;
EntityDialog.prototype.getToolbarButtons = function () {
    let buttons = originalEntityDialogGetToolbarButtons ? originalEntityDialogGetToolbarButtons.call(this) : [];
    if (buttons && Array.isArray(buttons)) {
        return buttons.filter(b => {
            const cls = (b.cssClass || "").toLowerCase();
            return !cls.includes("undelete-button") &&
                   !cls.includes("edit-button") &&
                   !cls.includes("clone-button") &&
                   !cls.includes("localization-button");
        });
    }
    return buttons;
};

const originalUpdateInterface = EntityDialog.prototype.updateInterface;
EntityDialog.prototype.updateInterface = function () {
    originalUpdateInterface?.call(this);
    if (this.toolbar) {
        this.toolbar.findButton('undelete-button')?.remove();
        this.toolbar.findButton('clone-button')?.remove();
        this.toolbar.findButton('localization-button')?.remove();
        this.toolbar.findButton('edit-button')?.remove();
    }
};

const originalOnDialogOpen = TemplatedDialog.prototype.onDialogOpen;
TemplatedDialog.prototype.onDialogOpen = function () {
    originalOnDialogOpen?.call(this);
    if (this.element) {
        const el = typeof this.element.get === 'function' ? this.element.get(0) : this.element;
        if (el && el.querySelectorAll) {
            el.querySelectorAll('.undelete-button, .clone-button, .localization-button, .edit-button').forEach((btn: HTMLElement) => {
                btn.style.display = 'none';
            });
        }
    }
};

window.onerror = ErrorHandling.runtimeErrorHandler;
window.addEventListener('unhandledrejection', ErrorHandling.unhandledRejectionHandler);

// Override Toastr prototype to show popup dialogs for errors and warnings instead of toasts
(Toastr.prototype as any).error = function (message: any, title: any, opt: any) {
    alertDialog(message);
    return null;
};

(Toastr.prototype as any).warning = function (message: any, title: any, opt: any) {
    alertDialog(message);
    return null;
};

// Override ErrorHandling.showServiceError to always use alert mode
const originalShowServiceError = ErrorHandling.showServiceError;
ErrorHandling.showServiceError = function (error: any, errorInfo: any, errorMode: any) {
    originalShowServiceError(error, errorInfo, "alert");
};

// Override jQuery validate to intercept form validation and show a detailed popup alert
const $ = (globalThis as any).jQuery || (globalThis as any).$;
if ($ && $.fn && $.fn.validate) {
    const originalValidate = $.fn.validate;
    $.fn.validate = function (options: any) {
        options = options || {};
        const originalInvalidHandler = options.invalidHandler;

        options.invalidHandler = function (event: any, validator: any) {
            // Temporarily stub Toastr.prototype.error to silence the generic Serenity message
            const originalToastrError = (Toastr.prototype as any).error;
            (Toastr.prototype as any).error = () => null;

            try {
                if (originalInvalidHandler) {
                    originalInvalidHandler(event, validator);
                }
            } finally {
                // Restore Toastr.prototype.error
                (Toastr.prototype as any).error = originalToastrError;
            }

            // Show detailed alert popup with the specific fields that have missing or invalid data
            if (validator && validator.errorList && validator.errorList.length) {
                const getFieldLabel = (element: HTMLElement) => {
                    let label = element.closest('.field')?.querySelector('.caption')?.textContent;
                    if (!label) {
                        let id = element.getAttribute('id');
                        if (id) {
                            label = document.querySelector('label[for="' + id + '"]')?.textContent;
                        }
                    }
                    if (!label) {
                        label = element.getAttribute('placeholder') || element.getAttribute('name') || 'Field';
                    }
                    return label.replace(/:$/, '').trim();
                };

                const errorMessages = validator.errorList.map((x: any) => {
                    const el = x.element && x.element.jquery ? x.element[0] : x.element;
                    const label = getFieldLabel(el);
                    return `• ${label}: ${x.message}`;
                });

                alertDialog("The following fields have invalid or missing data:\n\n" + errorMessages.join("\n"));
            }
        };

        return originalValidate.call(this, options);
    };
}
